<script setup lang="ts">
/**
 * Header section at the top of the matter-context modal's left
 * pane. Shows the matter's external ID, name, client, and an optional
 * "Show description" toggle.
 *
 * Client name and description come from the Matter API (resolved from the
 * clients table and matters.description). The external matter ID has no
 * backing column yet, so it stays mocked — see
 * docs_for_claude/matter_context/matter-context-frontend-design.md
 * (deferred item #1).
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from '../../stores/matterStore';

const { t } = useI18n();
const matterStore = useMatterStore();

const matterName = computed(() => matterStore.currentMatter?.Name ?? t('matterContext.header.untitledMatter'));

// Client name falls back to an em dash when absent; the description toggle
// is hidden entirely when there's no description.
const clientName = computed(() => matterStore.currentMatter?.ClientName?.trim() || '—');
const description = computed(() => matterStore.currentMatter?.Description?.trim() ?? '');
const hasDescription = computed(() => description.value.length > 0);

const descriptionExpanded = ref(false);

const toggleDescription = () => {
    descriptionExpanded.value = !descriptionExpanded.value;
};
</script>

<template>
    <div class="mc-matter-header">
        <h2 class="mc-matter-name">{{ matterName }}</h2>
        <div class="mc-matter-org">{{ clientName }}</div>
        <button
            v-if="hasDescription"
            type="button"
            class="mc-matter-description-toggle"
            @click="toggleDescription"
        >
            {{ descriptionExpanded ? t('matterContext.header.hideDescription') : t('matterContext.header.showDescription') }}
        </button>
        <p v-if="hasDescription && descriptionExpanded" class="mc-matter-description">
            {{ description }}
        </p>
    </div>
</template>
